//-- Copyright (c) 2021 TMT Insights Group, LLC
import { convertStylesToStyled } from '@/_utils/utils';
import { LabelValue } from '@/components/_common';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { TableCollapse } from '@/components/_common/Table';
import { useAppDispatch } from '@/redux/hooks';
import { clearSearchParams, getFolder, setPath, setTransferQueueDrawer } from '@/redux/slices/fileManagerSlice';
import styled from '@emotion/styled';
import { Grid, IconButton } from '@mui/material';

export type TransferQueueExpandProps = {
  isCollapsed: boolean;
  item: any;
};
const TransferQueueExpand = ({ isCollapsed, item }: TransferQueueExpandProps) => {
  const dispatch = useAppDispatch();

  const navigateTo = async (bucket: any, fullPath: any) => {
    const pathArray = [bucket, ...fullPath.split('/')].filter((part) => part.trim() !== '' && part.indexOf('.') === -1);
    let path: any[] = [];
    const promises: any[] = [];
    dispatch(clearSearchParams());
    for (const partialPath of pathArray) {
      path = path.concat(partialPath);
      const response = await dispatch(getFolder({ path })).unwrap();
      promises.push(response);
    }
    await Promise.all(promises);
    dispatch(setPath(pathArray));
    dispatch(setTransferQueueDrawer(false));
  };

  const sourceBucket = item.task_call?.args?.[0];
  const sourcePath = item.task_call?.args?.[1].substring(0, item.task_call?.args?.[1].lastIndexOf('/'));
  const targetBucket = item.task_call?.args?.[2];
  const targetPath = item.task_call?.args?.[3];

  return (
    <TableCollapse isCollapsed={isCollapsed}>
      <StyledGrid container spacing={5}>
        <Grid item xs={6}>
          <Grid container alignItems="center" justifyContent="flex-start">
            <StyledLabelValue label="Source Folder Path">
              <SourcePath>{sourcePath || ' - '}</SourcePath>
              {sourcePath && (
                <LinkButton onClick={() => navigateTo(sourceBucket, sourcePath)} color="secondary" size="small">
                  <PolarisIcon icon="external_link" height={18} />
                </LinkButton>
              )}
            </StyledLabelValue>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Grid container alignItems="center" justifyContent="flex-start">
            <StyledLabelValue label="Destination Folder Path">
              <SourcePath>{targetPath || ' - '}</SourcePath>
              {targetPath?.length > 0 && (
                <LinkButton onClick={() => navigateTo(targetBucket, targetPath)} color="secondary" size="small">
                  <PolarisIcon icon="external_link" height={18} />
                </LinkButton>
              )}
            </StyledLabelValue>
          </Grid>
        </Grid>
      </StyledGrid>
    </TableCollapse>
  );
};

export default TransferQueueExpand;

const SourcePath = styled.span`
  ${({ theme }) => convertStylesToStyled(theme?.components?.MuiTableCell?.styleOverrides?.root)}
  word-break: break-all;
  padding: 0;
`;

const StyledGrid = styled(Grid)`
  padding: ${({ theme }) => theme.spacing(0.5, 2, 0, 5)};
`;

const LinkButton = styled(IconButton)`
  margin-left: ${({ theme }) => theme.spacing(1)};

  &:hover {
    background: ${({ theme }) => theme.palette.action.focus};
  }

  & .MuiSvgIcon-root {
    font-size: 1rem;
  }
`;

const StyledLabelValue = styled(LabelValue)`
  white-space: nowrap;
`;
